import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from 'components/core/Modals/Modal/Modal';
import { Button, Grid, Icon, Input, Typhography } from 'components/core';
import {
  BatchApprovalRule,
  useCreateBatchApprovalRuleMutation,
  useUpdateBatchApprovalRuleMutation,
} from 'services/batchApprovalRulesApi';
import { useGetPropertiesQuery } from 'services/properties';
import { useGetUserCustomerRolesQuery } from 'services/userApi';
import MultiSelect, { MultiSelectItem } from './MultiSelect';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingRule: BatchApprovalRule | null;
}

interface FormValues {
  name: string;
  property_ids: string[];
  min_amount: string;
  max_amount: string;
  approver_user_ids: string[];
  approver_role_label: string;
  priority: number;
  active: boolean;
}

const schema = yup.object({
  name: yup.string().trim().required('Name is required'),
  property_ids: yup.array().of(yup.string()).default([]),
  min_amount: yup
    .string()
    .test('is-valid', 'Must be a non-negative number', (v) => {
      if (v === '' || v === undefined || v === null) return true;
      const n = Number(v);
      return Number.isFinite(n) && n >= 0;
    })
    .default(''),
  max_amount: yup
    .string()
    .test('is-valid', 'Must be a non-negative number', (v) => {
      if (v === '' || v === undefined || v === null) return true;
      const n = Number(v);
      return Number.isFinite(n) && n >= 0;
    })
    .default(''),
  approver_user_ids: yup
    .array()
    .of(yup.string())
    .min(1, 'At least one approver is required')
    .required(),
  approver_role_label: yup.string().trim().required('Role label is required'),
  priority: yup
    .number()
    .typeError('Priority must be a number')
    .integer('Must be a whole number')
    .min(0, 'Must be >= 0')
    .required(),
  active: yup.boolean().default(true),
});

const toFormValues = (rule: BatchApprovalRule | null): FormValues => ({
  name: rule?.name ?? '',
  property_ids: rule?.property_ids ?? [],
  min_amount:
    rule?.min_amount === null || rule?.min_amount === undefined ? '' : String(rule.min_amount),
  max_amount:
    rule?.max_amount === null || rule?.max_amount === undefined ? '' : String(rule.max_amount),
  approver_user_ids: rule?.approver_user_ids ?? [],
  approver_role_label: rule?.approver_role_label ?? '',
  priority: rule?.priority ?? 100,
  active: rule?.active ?? true,
});

const parseAmount = (v: string): number | null => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const RuleFormModal = ({ isOpen, onClose, onSuccess, editingRule }: Props) => {
  const [createRule, createState] = useCreateBatchApprovalRuleMutation();
  const [updateRule, updateState] = useUpdateBatchApprovalRuleMutation();

  const { data: propertiesData, isFetching: isPropertiesFetching } = useGetPropertiesQuery(
    { limit: 1000, offset: 0, orderBy: 'CREATED_AT_ASC' },
    { skip: !isOpen }
  );
  const { data: customerRolesData, isFetching: isUsersFetching } = useGetUserCustomerRolesQuery(
    {},
    { skip: !isOpen }
  );

  const properties: MultiSelectItem[] = useMemo(
    () =>
      (
        (propertiesData?.properties?.nodes ?? []) as Array<{
          id: string;
          name: string;
        }>
      ).map((p) => ({ id: p.id, primary: p.name })),
    [propertiesData]
  );
  const users: MultiSelectItem[] = useMemo(
    () =>
      (customerRolesData?.userCustomerRoles?.nodes ?? [])
        .filter((n: any) => String(n.role ?? '').toUpperCase() === 'ADMIN')
        .map((n: any) => {
          const fullName = [n.user.first_name, n.user.last_name].filter(Boolean).join(' ').trim();
          return {
            id: n.user.id,
            primary: fullName || n.user.email || n.user.id,
            secondary:
              fullName && n.user.email && n.user.email !== fullName ? n.user.email : undefined,
          };
        }),
    [customerRolesData]
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore — yup.default shapes are looser than TS infers
    resolver: yupResolver(schema),
    defaultValues: toFormValues(editingRule),
  });

  useEffect(() => {
    if (isOpen) reset(toFormValues(editingRule));
  }, [isOpen, editingRule, reset]);

  const isSaving = createState.isLoading || updateState.isLoading;
  const serverError =
    (createState.error as any)?.data?.message || (updateState.error as any)?.data?.message;

  const onSubmit = async (values: FormValues) => {
    const min = parseAmount(values.min_amount);
    const max = parseAmount(values.max_amount);
    if (min !== null && max !== null && min > max) {
      // eslint-disable-next-line no-alert
      window.alert('Min amount must be <= max amount');
      return;
    }

    const payload = {
      name: values.name.trim(),
      property_ids: values.property_ids.length ? values.property_ids : null,
      min_amount: min,
      max_amount: max,
      approver_user_ids: values.approver_user_ids,
      approver_role_label: values.approver_role_label.trim(),
      priority: Number(values.priority),
      active: values.active,
    };

    try {
      if (editingRule) {
        await updateRule({ id: editingRule.id, ...payload }).unwrap();
      } else {
        await createRule({ ...payload, customer_id: '' }).unwrap();
      }
      onSuccess();
    } catch {
      // serverError surfaces from mutation state
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalAdditionalClasses="!w-[560px] max-h-[86vh] overflow-y-auto items-center justify-center px-6 py-8 flex-col"
    >
      <Typhography
        component="p"
        type="h2"
        color="text-grey-900 mb-6 uppercase"
        additionalClasses="text-center"
      >
        {editingRule ? 'EDIT APPROVAL RULE' : 'NEW APPROVAL RULE'}
      </Typhography>

      <div className="absolute cursor-pointer top-7 right-4 text-grey-600" onClick={onClose}>
        <Icon iconColor="inherit" iconType="CloseIcon" />
      </div>

      {serverError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded w-full">
          {serverError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 w-full">
        {/* Priority hidden — defaulted to 100 for v1 */}
        <input type="hidden" {...register('priority', { valueAsNumber: true })} />

        <Grid columnCount={2} columnGap={2}>
          <Controller
            control={control}
            name="name"
            render={({ field: { value } }) => (
              <Input
                id="name"
                labelName="name"
                type="text"
                label="Name"
                {...register('name')}
                error={errors?.name?.message}
                value={value}
              />
            )}
          />
          <Controller
            control={control}
            name="approver_role_label"
            render={({ field: { value } }) => (
              <Input
                id="approver_role_label"
                labelName="approver_role_label"
                type="text"
                label="Role label"
                {...register('approver_role_label')}
                error={errors?.approver_role_label?.message}
                value={value}
              />
            )}
          />
        </Grid>

        <Grid columnCount={2} columnGap={2}>
          <Controller
            control={control}
            name="min_amount"
            render={({ field: { value } }) => (
              <Input
                id="min_amount"
                labelName="min_amount"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore — Input type union is narrow; native number works at runtime
                type="number"
                inputMode="decimal"
                step="0.01"
                min={0}
                label="Min amount (USD)"
                {...register('min_amount')}
                error={errors?.min_amount?.message}
                value={value}
              />
            )}
          />
          <Controller
            control={control}
            name="max_amount"
            render={({ field: { value } }) => (
              <Input
                id="max_amount"
                labelName="max_amount"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore — Input type union is narrow; native number works at runtime
                type="number"
                inputMode="decimal"
                step="0.01"
                min={0}
                label="Max amount (USD)"
                {...register('max_amount')}
                error={errors?.max_amount?.message}
                value={value}
              />
            )}
          />
        </Grid>

        <Controller
          name="property_ids"
          control={control}
          render={({ field }) => (
            <MultiSelect
              label="Property scope"
              hint={field.value.length === 0 ? 'All properties' : `${field.value.length} selected`}
              items={properties}
              selected={field.value}
              onChange={field.onChange}
              placeholder="Search properties…"
              emptyMessage={
                isPropertiesFetching
                  ? 'Loading properties…'
                  : 'No properties available — this rule will apply to all properties.'
              }
            />
          )}
        />

        <Controller
          name="approver_user_ids"
          control={control}
          render={({ field }) => (
            <MultiSelect
              label="Approvers"
              hint={
                field.value.length > 0
                  ? `${field.value.length} selected · any one can approve`
                  : 'any one can approve · admins only'
              }
              items={users}
              selected={field.value}
              onChange={field.onChange}
              placeholder="Search admins…"
              emptyMessage={
                isUsersFetching
                  ? 'Loading admins…'
                  : 'No admins available. Add an admin from Settings → Team.'
              }
              error={(errors.approver_user_ids as unknown as { message?: string })?.message}
            />
          )}
        />

        <div className="relative flex flex-col w-full form-input-wrapper">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="active"
              {...register('active')}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="active" className="text-14s font-normal text-grey-600 cursor-pointer">
              Active (uncheck to save as inactive)
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
          <Button
            type="submit"
            additionalClasses="px-4 py-2"
            variants="default"
            isDisabled={isSaving}
            buttonLabel={isSaving ? 'Saving…' : editingRule ? 'Save changes' : 'Create rule'}
          />
          <Button
            type="button"
            onClick={onClose}
            additionalClasses="px-4 py-2"
            variants="defaultOutlined"
            buttonLabel="Cancel"
          />
        </div>
      </form>
    </Modal>
  );
};

export default RuleFormModal;
